import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, Users } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EVENT_GAMES } from "@/lib/constants";

const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

export const EventTabs = ({ value, onChange, prefix = "events" }) => (
  <div className="flex flex-wrap gap-2">
    {["", ...EVENT_GAMES].map((g) => (
      <button key={g || "all"} onClick={() => onChange(g)} data-testid={`${prefix}-tab-${g || "all"}`}
        className={`rounded-full border-2 px-4 py-1.5 text-sm font-semibold transition-colors ${value === g ? "border-black bg-amber-500 text-black" : "border-black/10 bg-white text-stone-600 hover:border-black/40"}`}>
        {g || "Tutti"}
      </button>
    ))}
  </div>
);

export const EventRow = ({ e, onRegister }) => {
  const full = e.registered_count >= e.capacity;
  const d = new Date(`${e.date}T00:00:00`);
  return (
    <div className="flex flex-wrap items-center gap-5 rounded-2xl border border-black/10 bg-white p-5" data-testid={`event-${e.id}`}>
      <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-xl border-2 border-black bg-amber-500 text-black shadow-[3px_3px_0_#000]">
        <span className="font-display text-2xl font-bold leading-none">{d.getDate()}</span>
        <span className="text-[11px] font-bold uppercase">{d.toLocaleDateString("it-IT", { month: "short" })}</span>
      </div>
      <div className="min-w-[200px] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-violet-500/15 px-2.5 py-0.5 text-xs font-semibold text-violet-500">{e.game}</span>
          <span className="rounded-full bg-cyan-500/15 px-2.5 py-0.5 text-xs font-semibold text-cyan-700">{e.type}</span>
        </div>
        <p className="mt-1 font-display text-lg font-semibold">{e.title}</p>
        <p className="flex flex-wrap gap-x-4 text-sm text-stone-500">
          <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(e.date)}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{e.time}</span>
          <span className="flex items-center gap-1" data-testid={`event-count-${e.id}`}><Users className="h-3.5 w-3.5" />{e.registered_count}/{e.capacity}</span>
        </p>
        {e.description && <p className="mt-1 text-sm text-stone-600">{e.description}</p>}
      </div>
      <div className="flex flex-col items-end gap-2">
        <span className="font-mono font-bold text-amber-600">{e.price ? eur(e.price) : "Gratis"}</span>
        {onRegister && (
          <button onClick={() => onRegister(e)} disabled={full && !e.is_registered} data-testid={`event-register-${e.id}`}
            className={`sticker-btn rounded-full px-5 py-2 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-50 ${e.is_registered ? "bg-white text-black" : "bg-black text-white"}`}>
            {e.is_registered ? "Annulla iscrizione" : full ? "Al completo" : "Iscriviti"}
          </button>
        )}
      </div>
    </div>
  );
};

export default function EventsPage() {
  const { user } = useAuth();
  const [game, setGame] = useState("");
  const [events, setEvents] = useState(null);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    api.get("/events", { params: { game: game || undefined } }).then((r) => setEvents(r.data));
  }, [game]);
  useEffect(() => {
    if (user) api.get("/events/mine").then((r) => setMine(r.data.map((e) => e.id)));
  }, [user]);

  const register = async (e) => {
    if (!user) return toast.error("Accedi per iscriverti agli eventi");
    try {
      const { data } = await api.post(`/events/${e.id}/register`);
      setEvents((list) => list.map((x) => (x.id === e.id ? data : x)));
      setMine((m) => (data.is_registered ? [...m, e.id] : m.filter((i) => i !== e.id)));
      toast.success(data.is_registered ? "Iscrizione confermata!" : "Iscrizione annullata");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-8 sm:px-6 sm:py-12">
      <p className="font-display text-sm uppercase tracking-[0.2em] text-amber-600">Vieni a giocare da noi</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-5xl" data-testid="events-title">Calendario eventi</h1>
      <p className="mt-3 max-w-2xl text-stone-600">Tornei ufficiali, prerelease e serate casual nel nostro ampio spazio climatizzato. Iscriviti online per riservare il posto: la quota si paga in negozio.</p>
      {!user && <p className="mt-4 text-sm text-stone-600"><Link to="/accedi?next=/eventi" className="font-semibold text-amber-600 underline" data-testid="events-login-link">Accedi</Link> per iscriverti.</p>}
      <div className="mt-8"><EventTabs value={game} onChange={setGame} /></div>
      <div className="mt-6 space-y-4" data-testid="events-list">
        {events === null && <p className="text-stone-500">Caricamento...</p>}
        {events?.length === 0 && <p className="rounded-2xl border border-dashed border-black/15 p-10 text-center text-stone-500" data-testid="events-empty">Nessun evento in programma per questo gioco.</p>}
        {events?.map((e) => <EventRow key={e.id} e={{ ...e, is_registered: mine.includes(e.id) }} onRegister={register} />)}
      </div>
    </div>
  );
}
